import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { fmtDateMed } from "../../lib/dates";

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

/**
 * Flat "All Assignments" table shown below the calendar/grid.
 * Pure presentational — receives entries + lookup helpers from the parent.
 */
const RosterAssignmentsTable = ({ entries, getShift, getEmployeeName }) => {
  if (!entries || entries.length === 0) return null;

  return (
    <div
      className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800"
      data-testid="roster-assignments-table"
    >
      <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
        <h2 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">
          All Assignments
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
              <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">Date</th>
              <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">Employee</th>
              <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">Shift</th>
              <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">Time</th>
              <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {[...entries]
              .sort((a, b) =>
                a.date.localeCompare(b.date) ||
                a.employee_id.localeCompare(b.employee_id)
              )
              .map((entry) => {
                const shift = getShift(entry.shift_id);
                return (
                  <tr
                    key={entry.entry_id}
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                  >
                    <td className="py-3 px-4 text-sm tabular-nums text-zinc-900 dark:text-zinc-100">
                      {fmtDateMed(entry.date)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6 border border-zinc-200 dark:border-zinc-800">
                          <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-xs">
                            {getInitials(getEmployeeName(entry.employee_id))}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-zinc-900 dark:text-zinc-100">
                          {getEmployeeName(entry.employee_id)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: shift?.color }}
                        />
                        <span className="text-sm text-zinc-900 dark:text-zinc-100">
                          {shift?.name}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm tabular-nums text-zinc-600 dark:text-zinc-400">
                      {shift?.start_time} — {shift?.end_time}
                    </td>
                    <td className="py-3 px-4 text-sm text-zinc-500 dark:text-zinc-400">
                      {entry.notes || "—"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RosterAssignmentsTable;
